/**
 * Identity Details Layout
 *
 * Custom section layout for the applicant "Identity Details" step. Mirrors
 * SectionLayout (BaseLayout + action bar) but gates the "Next step" button
 * behind a shared name-matching validation gate (useNameMatchGate +
 * NameMismatchModal) when an identity document name is present in the form.
 *
 * Matches the WWCC pattern: clicking Next with a document name that doesn't
 * match the legal name or any previous/alias names opens the modal and does
 * not call submit(), so the applicant is blocked on this page until the name
 * is corrected.
 *
 * The gate is a no-op when no document name is populated — IDENTITY_DETAILS
 * is shared across checks (AU DL, UK DBS, Canadian criminal, etc.) and only
 * some of them collect a document name. For those that don't, submit passes
 * through untouched.
 */
import { APIError } from "@certn/api";
import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Space,
  SummaryCard,
  showToast,
  useColors,
} from "@certn/cascadia-react";
import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import { CascadiaJsonFormContext } from "@certn/forms";
import { Text } from "@certn/forms/src/components/Text";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { BaseLayoutContent } from "@certn/forms/src/layouts/BaseLayoutContent";
import { hasLabel } from "@certn/forms/src/utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { NameMismatchModal } from "../components/NameMismatchModal";
import { useNameMatchGate } from "../hooks/useNameMatchGate";
import { NameClaim } from "../utils/name-matching";
import { NotStarted } from "./NotStarted";
import { useApplication } from "../ApplicationContext";

interface IdentityDetailsValues {
  drivers_license?: {
    identity_document?: {
      name?: NameClaim;
    };
  };
}

/**
 * Returns the document name to gate on, or `undefined` if no identity
 * document with a name is populated in the form. As new checks add document
 * names (passport, UK DL, NDIS, etc.), extend this lookup.
 */
function getDocumentName(values: unknown): NameClaim | undefined {
  const identityValues = values as IdentityDetailsValues | undefined;
  const licenceName = identityValues?.drivers_license?.identity_document?.name;
  if (
    licenceName &&
    (licenceName.given_name?.trim() || licenceName.family_name?.trim())
  ) {
    return licenceName;
  }
  return undefined;
}

function IdentityDetailsLayout(props: CascadiaLayoutProps) {
  const { t } = useTranslation();
  const colors = useColors();
  const { isSummary, isSubmitted, readonly, isSubmitting, values, submit } =
    useContext(CascadiaJsonFormContext);
  const {
    prevSection,
    navigateToSection,
    partiallySaveData,
    completedSections,
  } = useApplication();

  const documentName = getDocumentName(values);
  const { showModal, setShowModal, gateSubmit } =
    useNameMatchGate(documentName);

  const handleNextStep = () => {
    if (!documentName) {
      submit();
      return;
    }
    gateSubmit(submit);
  };

  if (isSummary) {
    const sectionKey = (props.uischema as any)?.name;
    const isCompleted = completedSections?.includes(sectionKey);

    return (
      <SummaryCard header={props.label}>
        <BaseLayout
          testID="section-layout"
          {...props}
          className={styles.summary}
        >
          {!isCompleted && !readonly && (
            <NotStarted
              sectionKey={sectionKey}
              showErrors={Boolean(isSubmitted)}
            />
          )}
        </BaseLayout>
      </SummaryCard>
    );
  }

  return (
    <>
      <BaseLayout {...props}>
        <div className={styles.heading}>
          {hasLabel(props.uischema) && props.uischema.label && (
            <h1 className={styles.label}>{props.uischema.label}</h1>
          )}
          {props.uischema.options?.subLabel && (
            <Text style="form/body-primary" color="text-weak">
              {props.uischema.options.subLabel}
            </Text>
          )}
        </div>
        <BaseLayoutContent />
      </BaseLayout>
      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid ${colors["border-base"]}`,
        })}
      >
        <ButtonGroup justify="end">
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          <PrimaryButton
            type="button"
            variant="brand-primary"
            isLoading={isSubmitting}
            onPress={handleNextStep}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>
      <NameMismatchModal show={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}

const styles = {
  summary: css({
    display: "flex",
    flexDirection: "column",
    gap: 0,
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  label: css(getTextStyle("form/page-title"), { margin: 0 }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
};

IdentityDetailsLayout.displayName = "IdentityDetailsSection";

export const IdentityDetailsLayoutRenderer = withCascadiaLayoutProps(
  IdentityDetailsLayout
);
